import {
	applyClientSearch,
	openApClientSection,
	openHomeClientSection,
	openSiteClientSection,
} from './adapter';
import {
	buildClientDashboardPath,
	buildClientNavigationState,
	isClientNavigationReady,
} from './navigation';
import {openClientDisconnectModal} from './modal';
import {resolveClients} from './resolver';
import {readClientCounts} from './statistics';
import {serializeClientContext} from './context';

/**
 * Client.
 *
 * A connected device, and the only resource in Canvas whose list is not a
 * page. The same accordion is rendered on three dashboards - Home, Site and
 * Access Point - and which clients it shows is decided by the one you are
 * standing on, so there is no route to navigate to and no scope to pass:
 * the destination is the dashboard, and the section is the accordion on it.
 *
 * Active and Historical are a tab inside that accordion. This plugin owns
 * Active; Historical is a separate resource, the way Active and Historical
 * Alarms already are.
 *
 * One client does have a route, keyed by its MAC in the query string, and
 * that is the only page here that acts on a single record. Disconnect opens
 * Canvas's own confirmation and stops.
 */
const clientPlugin = {
	id: 'client',
	version: 1,
	commandVersion: 1,
	resource: 'client',
	operations: [
		'options',
		'list',
		'search',
		'open',
		'disconnect',
		'refresh',
		// Clearing the search box, which is a different thing from
		// `refresh`: that reloads the table as it is filtered, this puts
		// the whole list back. Both arrive as the filter handler below -
		// `applyClientSearch` sends an empty term, and `clients.jsx` reads
		// no term as the box being cleared rather than as a search that
		// finds nothing.
		'reset',
		'status_summary',
	],
	dependencies: {
		// A Site-scoped list looks the Site up with the Site plugin's
		// resolver, the same arrangement Access Points and Switches use.
		plugins: ['site', 'canvas.navigation'],
		resolvers: ['site'],
		destinations: ['home.dashboard', 'site.dashboard', 'ap.dashboard'],
	},
	destinations: {
		'client.dashboard': {
			path: buildClientDashboardPath,
			buildState: buildClientNavigationState,
		},
	},
	completionVerifiers: {
		'navigate:client.dashboard': isClientNavigationReady,
	},
	resolvers: {
		client: resolveClients,
	},
	sections: {
		'home.clients_active': openHomeClientSection,
		'site.clients_active': openSiteClientSection,
		'ap.clients_active': openApClientSection,
	},
	filters: {
		// Not a filter in the Canvas sense - the search box is the only
		// control that narrows Active by anything but the Site.
		client: (command) => applyClientSearch(command.params?.query),
	},
	statistics: {
		client: readClientCounts,
	},
	modals: {
		'client.disconnect': openClientDisconnectModal,
	},
	contextSerializers: {
		client: serializeClientContext,
	},
};

export default clientPlugin;
